import {format} from 'date-fns';
import {
  Button,
  Calendar,
  IconButton,
  Segment,
  Tooltip,
  useAnimatedPopper,
} from 'platform/components';
import {Text} from 'platform/foundation';
import {convertStringLocaleToDateFns, useDateTimeFormatter, useLocale} from 'platform/locale';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {RentalCalendarFilterValues} from '../types/RentalCalendarFilterValues';
import type {RentalCalendarView} from '../types/RentalCalendarView';
import {RentalCalendarFilters} from './RentalCalendarFilters';

interface RentalCalendarHeaderProps extends RequiredTestIdProps {
  currentDate: Date;
  view: RentalCalendarView;
  onNavigate: (direction: 'previous' | 'next' | 'today') => void;
  onDateChange: (date: Date) => void;
  onViewChange: (view: RentalCalendarView) => void;
  onRefresh: () => void;
  filters: RentalCalendarFilterValues;
  onFiltersChange: (filters: RentalCalendarFilterValues) => void;
}

export function RentalCalendarHeader(props: RentalCalendarHeaderProps) {
  const {language} = useLocale();
  const formatDateTime = useDateTimeFormatter();

  const {togglePopper, closePopper, referenceRef, popperProps, Popper} = useAnimatedPopper({
    placement: 'bottom-start',
    gutter: 4,
  });

  const handleDateChange = (date: Date | null) => {
    if (isNotNil(date)) {
      props.onDateChange(date);
      closePopper();
    }
  };

  const handleNavigate = (direction: 'previous' | 'next' | 'today') => {
    pushGtmEvent({
      event: 'button_click',
      event_id: `rental_calendar_navigate_${direction}_click`,
    });
    props.onNavigate(direction);
  };

  const title =
    props.view === 'month'
      ? format(props.currentDate, 'LLLL yyyy', {locale: convertStringLocaleToDateFns(language)})
      : formatDateTime('dateLong', props.currentDate);

  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      <Button
        data-testid={suffixTestId('header-todayButton', props)}
        variant="outlined"
        title={i18n.t('bigCalendar.today')}
        onClick={() => handleNavigate('today')}
      />
      <div ref={referenceRef}>
        <IconButton
          data-testid={suffixTestId('header-datePickerButton', props)}
          icon="action/date_range"
          onClick={togglePopper}
        />
      </div>
      <Popper {...popperProps} usePortal>
        <div className="rounded bg-palettes-white-10-100 p-2 shadow-lg">
          <Calendar
            value={props.currentDate}
            onChange={(value) => handleDateChange(Array.isArray(value) ? value[0] : value)}
            data-testid={suffixTestId('header-datePicker', props)}
          />
        </div>
      </Popper>
      <IconButton
        data-testid={suffixTestId('header-chevronLeftButton', props)}
        icon="navigation/chevron_left"
        onClick={() => handleNavigate('previous')}
      />
      <IconButton
        data-testid={suffixTestId('header-chevronRightButton', props)}
        icon="navigation/chevron_right"
        onClick={() => handleNavigate('next')}
      />
      <div className="min-w-40">
        <Text data-testid={suffixTestId('header-title', props)} size="base" alternative>
          {title}
        </Text>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <Tooltip label={i18n.t('general.actions.refresh')}>
          <IconButton
            data-testid={suffixTestId('header-refreshButton', props)}
            icon="navigation/refresh"
            aria-label={i18n.t('general.actions.refresh')}
            onClick={props.onRefresh}
          />
        </Tooltip>
        <RentalCalendarFilters
          data-testid={props['data-testid']}
          filters={props.filters}
          onFiltersChange={props.onFiltersChange}
        />
      </div>
      <div>
        <Segment<RentalCalendarView>
          data-testid={suffixTestId('header-viewSegment', props)}
          value={props.view}
          onChange={props.onViewChange}
          options={[
            {value: 'month', label: i18n.t('bigCalendar.month')},
            {value: 'day', label: i18n.t('bigCalendar.day')},
          ]}
        />
      </div>
    </div>
  );
}
